import ruLocale from '@fullcalendar/core/locales/ru';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import Loading from 'core/components/Loading';
import { getStoredCalendarView, setStoredCalendarView } from 'core/utils/calendarUtils';
import { dateFormatter, dateTimeFormatter } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import useOrderActionsLog from 'hooks/swr/useOrderActionLog';
import useOrderActionsNoDate from 'hooks/swr/useOrderActionNoDate';
import useResponsive from 'hooks/useResponsive';
import SimplePage from 'layout/pages/SimplePage/SimplePage';
import { isNil, pick } from 'lodash';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import StoreContext from 'stores/context/StoreContext';
import CalendarHeader from './CalendarHeader';
import EventDetailDialog from './EventDetailDialog';
import Legend from './Legend';
import MonthlyListView from './MonthlyListView/MonthlyListView';
import OrdersWithoutDateList from './OrdersWithoutDateList';
import { getActionColor } from './utils';
import type { LegendItem } from './Legend';
import type { EventClickArg, EventInput } from '@fullcalendar/core';
import type { OrderStateEnum } from 'models/enums/orderStateEnum';
import type { GeolocationCoordinates } from 'models/order/order';
import type { OrderActionsLog } from 'models/orderActionsLog';
import type { FC } from 'react';
import type { GetOrderActionsLogRequest } from 'services/dtoModels/orderActionLog/getOrderActionsLogRequest';

export interface EventExtendedProps {
  eventName: string;
  dateCreated: string;
  action: string;
  dateAction: string;
  actionCode: OrderActionEnum;
  orderId: number;
  orderNumber: string;
  orderState: OrderStateEnum;
  orderDateCreated: string;
  orderAddress: string;
  orderGeolocation?: GeolocationCoordinates;
  clientId: number;
  clientName: string;
  clientPhone: string;
  userId?: number;
  userFullName?: string;
}

export type CalendarView = 'dayGridMonth' | 'monthlyList';

export interface YearNavigationProps {
  year: number;
  onPrevYear: () => void;
  onNextYear: () => void;
  onCurrentYear: () => void;
}

const drawerWidth = 200;

const legendInit: LegendItem[] = [
  OrderActionEnum.Measurement,
  OrderActionEnum.Monolith,
  OrderActionEnum.Installation,
].map((a) => ({
  title: getOrderActionName(a),
  color: getActionColor(a),
  action: a,
  selected: true,
}));

const CalendarPage: FC = () => {
  const [currentView, setCurrentView] = useState<CalendarView>(getStoredCalendarView());
  const [legendItems, setLegendItems] = useState<LegendItem[]>(legendInit);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventExtendedProps>();
  const isUpSmScreen = useResponsive('up', 'sm');
  const isUpLgScreen = useResponsive('up', 'lg');

  const { shopStore } = useContext(StoreContext);

  useEffect(() => {
    setStoredCalendarView(currentView);
  }, [currentView]);

  const setStartOfDay = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const setEndOfDay = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };

  const getInitialDateRange = (
    view: CalendarView
  ): Pick<GetOrderActionsLogRequest, 'from' | 'to'> => {
    const now = new Date();
    if (view === 'monthlyList') {
      const start = setStartOfDay(new Date(now));
      start.setDate(start.getDate() - 3);
      const end = setEndOfDay(new Date(now));
      end.setDate(end.getDate() + 30);
      return { from: start, to: end };
    } else {
      const monthRange = {
        from: setStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: setEndOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
      return monthRange;
    }
  };

  const [dateRange, setDateRange] = useState<
    Pick<GetOrderActionsLogRequest, 'from' | 'to'>
  >(getInitialDateRange(currentView));

  const eventRequest: GetOrderActionsLogRequest = useMemo(() => {
    if (!isNil(dateRange)) {
      return {
        ...pick(dateRange, 'from', 'to'),
        actions: [
          OrderActionEnum.Measurement,
          OrderActionEnum.Monolith,
          OrderActionEnum.Installation,
        ],
      };
    }
    return {
      actions: [
        OrderActionEnum.Measurement,
        OrderActionEnum.Monolith,
        OrderActionEnum.Installation,
      ],
    };
  }, [dateRange]);

  const shouldLoadData = currentView === 'monthlyList' || !isNil(dateRange);
  const { data, isLoading } = useOrderActionsLog(eventRequest, shouldLoadData);
  const [filteredData, setFilteredData] = useState<OrderActionsLog[]>([]);

  const calendarRef = useRef<FullCalendar>(null);

  const selectedActions = useMemo(
    () => legendItems.filter((a) => a.selected).map((a) => a.action),
    [legendItems]
  );

  const [ordersWithoutDatePage, setOrdersWithoutDatePage] = useState(0);

  const { data: ordersWithoutDate, isLoading: ordersWithoutDateLoading } =
    useOrderActionsNoDate(
      {
        actions: selectedActions,
        pageIndex: ordersWithoutDatePage,
        pageSize: 5,
      },
      selectedActions.length > 0
    );

  const hasOrdersWithoutDate =
    !ordersWithoutDateLoading && (ordersWithoutDate?.totalCount ?? 0) > 0;

  const showOrdersWithoutActionDate =
    (hasOrdersWithoutDate &&
      shopStore.shop?.settings?.showOrdersWithoutActionDateInCalendar) ??
    true;

  useEffect(() => {
    const availableActions = legendItems.filter((a) => a.selected).map((a) => a.action);
    setFilteredData(data?.filter((d) => availableActions.includes(d.action)) ?? []);
  }, [data, legendItems]);

  const handleLegendChange = (items: LegendItem[]): void => {
    setLegendItems(items);

    const availableActions = items.filter((a) => a.selected).map((a) => a.action);
    setFilteredData(data?.filter((d) => availableActions.includes(d.action)) ?? []);
  };

  const getEventName = (action: OrderActionsLog): string =>
    `Заказ ${action.order.number}` +
    (isUpSmScreen
      ? isUpLgScreen
        ? `, ${getOrderActionName(action.action)}, ${action.order.client.fullName}`
        : `, ${getOrderActionName(action.action)}`
      : '');

  const events: EventInput = filteredData
    ?.filter((a) => !isNil(a.dateAction))
    .map((action) => ({
      title: getEventName(action),
      start: action.dateAction ?? action.dateCreate,
      end: action.dateAction ?? action.dateCreate,
      backgroundColor: getActionColor(action.action),
      extendedProps: {
        eventName: getEventName(action),
        dateAction: !isNil(action.dateAction)
          ? dateFormatter.format(action.dateAction)
          : '',
        dateCreated: dateTimeFormatter.format(action.dateCreate),
        action: getOrderActionName(action.action),
        orderId: action.order.id,
        orderNumber: action.order.number,
        orderState: action.order.state,
        orderDateCreated: dateTimeFormatter.format(action.order.dateCreate),
        orderAddress: action.order.address,
        orderGeolocation: action.order.geolocation,
        clientId: action.order.client.id,
        clientName: action.order.client.fullName,
        clientPhone: action.order.client.phone,
        userId: action.user?.id,
        userFullName: action.user?.fullName,
      },
    }));

  const handleEventClick = (info: EventClickArg): void => {
    setSelectedEvent(info.event.extendedProps as EventExtendedProps);
    setDialogOpen(true);
  };

  const RelativeBackdrop = styled(Backdrop)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.background.paper, 0.56),
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.drawer + 1,
  }));

  const viewChangeHandler = (view: CalendarView): void => {
    setCurrentView(view);
    const newDateRange = getInitialDateRange(view);
    setDateRange(newDateRange);

    if (view !== 'monthlyList' && calendarRef.current) {
      calendarRef.current.getApi().changeView(view);
      calendarRef.current.getApi().gotoDate(newDateRange.from ?? new Date());
    }
  };

  const getTitle = useMemo((): string => {
    switch (currentView) {
      case 'dayGridMonth':
        return `${
          dateRange?.from?.toLocaleString('default', {
            month: 'long',
          }) ?? ''
        } ${dateRange?.to?.getFullYear() ?? ''}`;
      default:
        return '';
    }
  }, [dateRange, currentView]);

  return (
    <SimplePage title='Календарь'>
      <RelativeBackdrop open={isLoading}>
        <Loading />
      </RelativeBackdrop>

      <Box id='calendar-drawer-root' sx={{ display: 'flex', flexGrow: 1 }}>
        <Stack
          direction={'column'}
          gap={2}
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
          <Legend items={legendItems} onChange={handleLegendChange} width={drawerWidth} />

          {showOrdersWithoutActionDate && (
            <Paper
              sx={{
                mr: 2,
                p: 2,
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}>
              <Typography variant='body1' fontWeight={'medium'} gutterBottom>
                Заказы без даты этапа
              </Typography>
              <OrdersWithoutDateList
                data={ordersWithoutDate}
                onPageChange={setOrdersWithoutDatePage}
              />
            </Paper>
          )}
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: { md: `calc(100% - ${drawerWidth}px)` },
          }}>
          <Paper sx={{ mb: 2 }}>
            <CalendarHeader
              title={getTitle}
              view={currentView}
              startDate={dateRange?.from ?? new Date()}
              endDate={dateRange?.to ?? new Date()}
              onDateRangeChange={(range) => {
                setDateRange({
                  from: range.start,
                  to: range.end,
                });
                if (currentView !== 'monthlyList') {
                  calendarRef.current?.getApi().gotoDate(range.start);
                }
              }}
              onViewChange={viewChangeHandler}
            />
          </Paper>

          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Legend items={legendItems} onChange={handleLegendChange} compact={true} />
          </Box>

          {showOrdersWithoutActionDate && (
            <Paper sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Accordion
                disableGutters
                sx={{
                  '& .MuiAccordionSummary-root': {
                    backgroundColor: (theme) => alpha(theme.palette.warning.light, 0.1),
                  },
                }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='panel1-content'
                  id='panel1-header'>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <WarningAmberIcon color='warning' />
                    <Typography component='span' color='warning.main' fontWeight='medium'>
                      Есть заказы без даты этапа
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <OrdersWithoutDateList
                    data={ordersWithoutDate}
                    onPageChange={setOrdersWithoutDatePage}
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}

          <Paper
            sx={(theme) => ({
              flex: 'auto',
              minHeight: '300px',
              '& .fc-event': {
                cursor: 'pointer',
              },
              '& .fc-view-harness': {
                backgroundColor: theme.palette.background.paper,
              },
              [theme.breakpoints.up('md')]: {
                '& .fc-event-title': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              },
              [theme.breakpoints.down('sm')]: {
                '.fc-header-toolbar': {
                  paddingX: 2,
                },
              },
            })}>
            {currentView === 'monthlyList' ? (
              <MonthlyListView
                data={filteredData}
                isLoading={isLoading}
                selectedActions={legendItems
                  .filter((a) => a.selected)
                  .map((a) => a.action)}
                dateRange={{
                  start: dateRange?.from ?? new Date(),
                  end: dateRange?.to ?? new Date(),
                }}
              />
            ) : (
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView='dayGridMonth'
                allDaySlot={false}
                height={'100%'}
                locale={'ru'}
                locales={[ruLocale]}
                headerToolbar={false}
                editable={false}
                selectable={false}
                events={events}
                eventClick={handleEventClick}
              />
            )}
          </Paper>
        </Box>
      </Box>
      {selectedEvent && (
        <EventDetailDialog
          event={selectedEvent}
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
          }}
        />
      )}
    </SimplePage>
  );
};

export default CalendarPage;
